<template>
    <ui-modal
        ref="modal"
        title="Manage Templates"
        size="large"
        :contain-focus="false"
        dismiss-on="close-button esc"
    >
        <div v-if="!isLoading" class="template-selection-container">
            <div class="template-list">
                <div 
                    v-for="template in templates" 
                    :key="template.id"
                    class="template-item"
                    :class="{ 
                        'selected': selectedTemplateId === template.id,
                        'disabled': !isTemplateAvailable(template)
                    }"
                    @click="selectTemplate(template)"
                >
                    <div class="template-header">
                        <div class="template-name">{{ template.name }}</div>
                        <ui-icon 
                            v-if="!isTemplateAvailable(template)"
                            class="warning-icon"
                            :tooltip="getUnavailableReason(template)"
                        >warning</ui-icon>
                    </div>
                    <div class="template-format">{{ template.format }}</div>
                </div>
                <div v-if="templates.length === 0" class="no-templates">
                    No templates found
                </div>
            </div>
            <div class="template-preview">
                <div v-if="selectedTemplate" class="preview-container">
                    <div class="preview-header">
                        <strong>{{ selectedTemplate.name }}</strong>
                    </div>
                    <div class="preview-content">
                        <img 
                            v-if="previewImageUrl" 
                            :src="previewImageUrl" 
                            alt="Template Preview"
                            class="preview-image"
                        />
                        <div v-else class="preview-loading">
                            <cog-loader></cog-loader>
                        </div>
                    </div>
                </div>
                <div v-else class="no-preview">
                    Select a template to preview
                </div>
            </div>
        </div>
        <div v-else class="loading-container">
            <cog-loader></cog-loader>
        </div>

        <div slot="footer" style="width: 100%; text-align: right;">
            <ui-button
                color="default"
                @click="close"
                :disabled="isLoading || isRendering"
            >
                Cancel
            </ui-button>
            <ui-button
                color="primary"
                :loading="isRendering"
                :disabled="!selectedTemplate || isLoading || !isTemplateAvailable(selectedTemplate)"
                @click="useTemplate"
            >
                Use Template
            </ui-button>
        </div>

        <!-- Data Source Selection Modal -->
        <ui-modal
            ref="dataSourceModal"
            title="Select Data Source"
            size="small"
            :contain-focus="false"
            dismiss-on="close-button esc"
        >
            <div class="data-source-list">
                <p>Multiple options are available for this template. Please select which one to use:</p>
                <div 
                    v-for="option in dataSourceOptions" 
                    :key="option.id"
                    class="data-source-option"
                    @click="selectDataSourceOption(option)"
                >
                    <div class="option-title">{{ option.title || option.description || `Option ${option.id}` }}</div>
                    <div v-if="option.registration" class="option-detail">{{ option.registration }}</div>
                    <div v-if="option.make && option.model" class="option-detail">{{ option.make }} {{ option.model }}</div>
                </div>
            </div>

            <div slot="footer" style="width: 100%; text-align: right;">
                <ui-button
                    color="default"
                    @click="closeDataSourceModal"
                >
                    Cancel
                </ui-button>
            </div>
        </ui-modal>
    </ui-modal>
</template>

<script>
    import DealerKit from '../../api'

    export default {
        name: 'TemplateManagementModal',
        data() {
            return {
                isLoading: false,
                isRendering: false,
                templates: [],
                selectedTemplateId: null,
                selectedTemplate: null,
                previewImageUrl: null,
                format: null,
                contactId: null,
                availableDataSources: null,
                dataSourceOptions: [],
            }
        },
        methods: {
            open(contactId, format) {
                this.contactId = contactId
                this.format = format
                this.selectedTemplateId = null
                this.selectedTemplate = null
                this.previewImageUrl = null
                this.availableDataSources = null
                
                this.$refs.modal.open()
                this.loadTemplates()
                this.loadAvailableDataSources()
            },
            async openWithTemplate(contactId, format, template) {
                this.contactId = contactId
                this.format = format
                this.selectedTemplateId = template.id
                this.selectedTemplate = template
                this.previewImageUrl = null
                
                await this.loadAvailableDataSources()
                
                const dataSourceType = this.getDataSourceType(template)
                const dataSource = this.availableDataSources[dataSourceType]
                
                if (dataSource && dataSource.options && dataSource.options.length > 1) {
                    this.dataSourceOptions = dataSource.options
                    this.$refs.dataSourceModal.open()
                } else if (dataSource && dataSource.options && dataSource.options.length === 1) {
                    await this.renderTemplate(template.data_source, dataSource.options[0].id)
                }
            },
            close() {
                this.$refs.modal.close()
            },
            closeDataSourceModal() {
                this.$refs.dataSourceModal.close()
            },
            async loadTemplates() {
                this.isLoading = true
                try {
                    // Fetch templates with datasources starting with inbox.conversation
                    const dataSources = [
                        'inbox.conversation.lead',
                        'inbox.conversation.vehicle_of_interest',
                        'inbox.conversation.part_exchange_offered',
                        'inbox.conversation.vehicle_reserved',
                        'inbox.conversation.appointment'
                    ].join(',')
                    
                    const response = await DealerKit.templates.getTemplates(1, dataSources, this.format)
                    this.templates = response.data.data
                } catch (error) {
                    console.error('Failed to load templates:', error)
                } finally {
                    this.isLoading = false
                }
            },
            async loadAvailableDataSources() {
                try {
                    const response = await DealerKit.crm.getContactTemplateDataSources(this.contactId)
                    this.availableDataSources = response.data.data
                } catch (error) {
                    console.error('Failed to load available data sources:', error)
                    this.availableDataSources = {} // Initialize to empty object on failure
                }
            },
            getDataSourceType(template) {
                // Extract the last part of the data_source (e.g., "vehicle_of_interest" from "inbox.conversation.vehicle_of_interest")
                const parts = template.data_source.split('.')
                return parts[parts.length - 1]
            },
            isTemplateAvailable(template) {
                if (!this.availableDataSources) {
                    return false // Don't assume available if data sources not loaded yet
                }
                
                const dataSourceType = this.getDataSourceType(template)
                const dataSource = this.availableDataSources[dataSourceType]
                
                return dataSource && dataSource.available
            },
            getUnavailableReason(template) {
                if (!this.availableDataSources) {
                    return 'Checking availability...'
                }
                
                const dataSourceType = this.getDataSourceType(template)
                const dataSource = this.availableDataSources[dataSourceType]
                
                if (!dataSource || !dataSource.available) {
                    return `No ${dataSourceType.replace(/_/g, ' ')} available for this conversation`
                }
                
                return ''
            },
            async selectTemplate(template) {
                if (!this.isTemplateAvailable(template)) {
                    return
                }
                
                this.selectedTemplateId = template.id
                this.selectedTemplate = template
                this.previewImageUrl = null
                
                if (!this.availableDataSources) {
                    return
                }

                // Load preview if we have a data source option
                const dataSourceType = this.getDataSourceType(template)
                const dataSource = this.availableDataSources[dataSourceType]
                
                if (dataSource && dataSource.options && dataSource.options.length > 0) {
                    // Use the first option for preview
                    const firstOption = dataSource.options[0]
                    this.loadPreview(template, template.data_source, firstOption.id)
                }
            },
            async loadPreview(template, dataSource, dataSourceId) {
                try {
                    const response = await DealerKit.templates.renderTemplate(template.id, dataSource, dataSourceId, {
                        headers: {
                            'Accept': 'image/png'
                        },
                        responseType: 'blob'
                    })
                    
                    this.previewImageUrl = URL.createObjectURL(response.data)
                } catch (error) {
                    console.error('Failed to load template preview:', error)
                }
            },
            async useTemplate() {
                if (!this.selectedTemplate || !this.isTemplateAvailable(this.selectedTemplate) || !this.availableDataSources) {
                    return
                }
                
                const dataSourceType = this.getDataSourceType(this.selectedTemplate)
                const dataSource = this.availableDataSources[dataSourceType]
                
                if (!dataSource || !dataSource.options || dataSource.options.length === 0) {
                    return
                }
                
                // If there are multiple options, show selection modal
                if (dataSource.options.length > 1) {
                    this.dataSourceOptions = dataSource.options
                    this.$refs.dataSourceModal.open()
                } else {
                    // Use the single option
                    await this.renderTemplate(this.selectedTemplate.data_source, dataSource.options[0].id)
                }
            },
            async selectDataSourceOption(option) {
                this.$refs.dataSourceModal.close()
                await this.renderTemplate(this.selectedTemplate.data_source, option.id)
            },
            async renderTemplate(dataSource, dataSourceId) {
                this.isRendering = true
                try {
                    const response = await DealerKit.templates.renderTemplate(this.selectedTemplate.id, dataSource, dataSourceId, {
                        headers: {
                            'Accept': this.selectedTemplate.format === 'html' ? 'text/html' : 'text/plain'
                        },
                    })

                    this.$emit('selected', {
                        template: this.selectedTemplate,
                        content: response.data
                    })
                    this.close()
                } catch (error) {
                    console.error('Failed to render template:', error)
                } finally {
                    this.isRendering = false
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .template-selection-container {
        display: flex;
        gap: 1rem;
        min-height: 400px;
    }

    .template-list {
        flex: 0 0 300px;
        border-right: 1px solid #e0e0e0;
        padding-right: 1rem;
        overflow-y: auto;
        max-height: 500px;
    }

    .template-item {
        padding: 0.75rem;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        margin-bottom: 0.5rem;
        cursor: pointer;
        transition: all 0.2s;

        &:hover:not(.disabled) {
            background-color: #f5f5f5;
            border-color: #2196F3;
        }

        &.selected {
            background-color: #e3f2fd;
            border-color: #2196F3;
        }

        &.disabled {
            opacity: 0.5;
            cursor: not-allowed;
            background-color: #f9f9f9;
        }
    }

    .template-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .template-name {
        font-weight: 500;
        margin-bottom: 0.25rem;
        flex: 1;
    }

    .warning-icon {
        color: #ff9800;
        font-size: 1.2rem;
        margin-left: 0.5rem;
    }

    .template-format {
        font-size: 0.875rem;
        color: #666;
        text-transform: uppercase;
    }

    .template-preview {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #f9f9f9;
        border-radius: 4px;
        padding: 1rem;
    }

    .preview-container {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .preview-header {
        margin-bottom: 1rem;
        padding-bottom: 0.5rem;
        border-bottom: 1px solid #e0e0e0;
    }

    .preview-content {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: auto;
    }

    .preview-image {
        max-width: 100%;
        max-height: 100%;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
    }

    .preview-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 200px;
    }

    .no-preview,
    .no-templates {
        text-align: center;
        color: #999;
        padding: 2rem;
    }

    .loading-container {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 400px;
    }

    .data-source-list {
        padding: 1rem 0;
    }

    .data-source-option {
        padding: 1rem;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        margin-bottom: 0.5rem;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
            background-color: #f5f5f5;
            border-color: #2196F3;
        }
    }

    .option-title {
        font-weight: 500;
        margin-bottom: 0.25rem;
    }

    .option-detail {
        font-size: 0.875rem;
        color: #666;
    }
</style>
